import React, { FC, ReactNode, createContext, useContext } from 'react';

export interface ICoverSlideshowContextValue {
    /** The fully loaded slideshow to render as the cover. */
    slideshow: SpApi.IEventSlideshow;
    /** Brand watermark object URL, or undefined when the slideshow has no watermark. */
    watermarkUrl?: string;
    /** Shared gallery music volume (0..1). */
    volume: number;
    /** Shared gallery music mute state. */
    isMuted: boolean;
    /** Reports this slideshow claiming/releasing exclusive audio, so gallery music can duck. */
    handleAudioActiveChange: (isActive: boolean) => void;
}

/**
 * Carries an already-loaded cover slideshow down to `CoverSlideshowBackground`.
 *
 * The slideshow is deliberately fetched *above* the intro page's loading gate (see `Intro`)
 * rather than inside the background component itself: the intro page holds its loading state
 * until the slideshow can start playing immediately, and a component rendered only *after* that
 * gate opens could never have loaded the data the gate is waiting on. Hoisting the fetch also
 * means layouts that mount more than one `CoverPhoto` (the responsive desktop/mobile variants in
 * `BlockLeft`/`BlockRight`/`Banner`) share a single request instead of firing one each.
 *
 * Defaults to `null` so consumers can tell "a cover slideshow is loaded and ready to play" from
 * "there is none" (a plain photo cover, a visitor who prefers reduced motion, or a surface with
 * no client-gallery services at all, such as the Studio branding preview).
 */
const CoverSlideshowContext = createContext<ICoverSlideshowContextValue | null>(null);

interface ICoverSlideshowProviderProps {
    value: ICoverSlideshowContextValue | null;
    children: ReactNode;
}

export const CoverSlideshowProvider: FC<ICoverSlideshowProviderProps> = ({ value, children }) => (
    <CoverSlideshowContext.Provider value={value}>{children}</CoverSlideshowContext.Provider>
);

/**
 * Returns the loaded cover slideshow, or `null` when there isn't one — callers should treat
 * `null` as "render the static cover photo instead", not an error.
 */
export const useCoverSlideshowContext = (): ICoverSlideshowContextValue | null =>
    useContext(CoverSlideshowContext);
